/**
 * Frontend API client - Cross-Channel WhatsApp Handoff + Contact Memory
 * (Phase H7).
 *
 * Thin typed wrappers around the H3 and H5 worker routes:
 *   - POST /api/handoffs/whatsapp-link
 *   - GET  /api/handoffs/:token
 *   - GET  /api/contacts/:id/memory
 *
 * Kept in its own file so the existing `api-client.ts` stays focused on
 * the transport (fetch + auth + 401 handling). All methods unwrap the
 * Worker's `{ ok, data }` envelope via `api.get` / `api.post`.
 */

import { api } from "./api-client";
import type {
  Channel,
  ContactInterest,
  ConversationSummary,
  CrossChannelContext,
  NextAction,
  SourceChannel,
} from "@ai-leasing-os/shared";

// ---------------------------------------------------------------------------
// POST /api/handoffs/whatsapp-link
// ---------------------------------------------------------------------------

export interface CreateWhatsappHandoffLinkInput {
  sourceChannel: SourceChannel;
  sourceContactId?: string;
  sourceConversationId?: string;
  sourcePropertyId?: string;
  sourceListing?: string;
  campaignId?: string;
  ref?: string;
  targetPhone?: string;
  message?: string;
  mode?: "handoff" | "ref_only";
  ttlHours?: number;
}

export type HandoffLinkResult =
  | {
      mode: "handoff";
      waUrl: string;
      handoffToken: string;
      expiresAt: string;
      ref: string | null;
      campaignId: string | null;
    }
  | {
      mode: "ref_only";
      waUrl: string;
      ref: string | null;
      campaignId: string | null;
    };

export function createWhatsappHandoffLink(
  input: CreateWhatsappHandoffLinkInput,
): Promise<HandoffLinkResult> {
  return api.post<HandoffLinkResult>("/api/handoffs/whatsapp-link", input);
}

// ---------------------------------------------------------------------------
// GET /api/handoffs/:token
// ---------------------------------------------------------------------------

export interface SafeHandoffStatus {
  tokenMasked: string;
  status: "active" | "consumed" | "expired" | "revoked";
  sourceChannel: string;
  sourceListing: string | null;
  sourcePropertyId: string | null;
  campaignId: string | null;
  ref: string | null;
  expiresAt: string;
  createdAt: string;
  consumedAt: string | null;
  originatingContactId: string | null;
  originatingConversationId: string | null;
  consumedByConversationId: string | null;
}

export function getHandoffStatus(token: string): Promise<SafeHandoffStatus> {
  return api.get<SafeHandoffStatus>(
    `/api/handoffs/${encodeURIComponent(token)}`,
  );
}

// ---------------------------------------------------------------------------
// GET /api/contacts/:id/memory
// ---------------------------------------------------------------------------

export function getContactMemory(
  contactId: string,
): Promise<CrossChannelContext> {
  return api.get<CrossChannelContext>(
    `/api/contacts/${encodeURIComponent(contactId)}/memory`,
  );
}

// Re-exports so callers can stay in this module and not chase shared types.
export type {
  Channel,
  ContactInterest,
  ConversationSummary,
  CrossChannelContext,
  NextAction,
  SourceChannel,
};
